import React from "react";
import { Truck, FileText, Plus, ArrowRight } from "lucide-react";
import { SHIPMENT_STATUS_LABELS } from "@/data/services";

function Stat({ label, value, sub }) {
  return (
    <div className="bg-white border border-slate-200 p-6">
      <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">{label}</div>
      <div className="mt-2 font-heading text-3xl font-black text-slate-900">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

export function Overview({ client, quotes, shipments, onJump }) {
  const openQuotes = quotes.filter((q) => q.status !== "closed").length;
  const activeShipments = shipments.filter((s) => s.current_status !== "delivered").length;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Stat label="Open Quotes" value={openQuotes} sub={`${quotes.length} total`} />
      <Stat label="Active Shipments" value={activeShipments} sub={`${shipments.length} total`} />
      <Stat label="Company" value={client.company_name || "—"} sub={client.business_type || ""} />

      <div className="lg:col-span-2 border border-slate-200 bg-white p-6">
        <div className="text-xs uppercase tracking-[0.25em] font-bold text-primary-brand mb-4">Recent Activity</div>
        {shipments.slice(0, 3).length + quotes.slice(0, 3).length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">No activity yet. Submit your first quote below.</div>
        ) : (
          <ul className="space-y-3">
            {shipments.slice(0, 3).map((s) => (
              <li key={s.id} className="flex items-center gap-3 text-sm border-b border-slate-100 pb-3">
                <Truck className="h-4 w-4 text-primary-brand flex-none" />
                <div className="flex-1">
                  <div className="font-heading font-bold text-slate-900">{s.tracking_number}</div>
                  <div className="text-slate-500 text-xs">{s.origin} → {s.destination}</div>
                </div>
                <span className="text-xs uppercase tracking-[0.2em] font-bold text-primary-brand">
                  {SHIPMENT_STATUS_LABELS[s.current_status]}
                </span>
              </li>
            ))}
            {quotes.slice(0, 3).map((q) => (
              <li key={q.id} className="flex items-center gap-3 text-sm border-b border-slate-100 pb-3 last:border-0">
                <FileText className="h-4 w-4 text-primary-brand flex-none" />
                <div className="flex-1">
                  <div className="font-heading font-bold text-slate-900">{q.service_type}</div>
                  <div className="text-slate-500 text-xs">{q.pickup_location} → {q.dropoff_location}</div>
                </div>
                <span className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">{q.status}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="border border-slate-200 bg-white p-6 flex flex-col justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-primary-brand mb-2">Need to ship?</div>
          <div className="font-heading text-xl font-black text-slate-900">Send a new quote request.</div>
          <p className="text-sm text-slate-500 mt-2">Your account info is pre-filled. Just tell us the route.</p>
        </div>
        <button
          onClick={() => onJump("new")}
          data-testid="overview-new-quote-cta"
          className="mt-6 inline-flex items-center justify-center gap-2 bg-primary-brand hover:bg-[#E04F00] text-white px-6 py-3 font-bold uppercase tracking-wider text-xs"
        >
          <Plus className="h-4 w-4" /> New Quote <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
